import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../css/AddToCart.css";
import { SkeletonRow } from "../components/Skeleton";
import { getProduct } from "../Utils/productCache";
import { refreshAccessToken } from "../Utils/authHelpers";
import { API_BASE_URL } from "../config/constants";
import {
  getGuestCart,
  updateGuestCartQty,
  removeGuestCartItem,
} from "../Utils/guestCart";
import { getOptimizedImageUrl } from "../Utils/upload";

// Utility function to get cookie by name
const getCookie = (name) => {
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return null;
  } catch (error) {
    return null;
  }
};

function AddToCart() {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stockError, setStockError] = useState(null); // e.g. { itemKey, message }
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [showInvalidDiscountPopup, setShowInvalidDiscountPopup] =
    useState(false);
  const [userId, setUserId] = useState(null);
  const [isGuest, setIsGuest] = useState(false);
  const navigate = useNavigate();

  const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  const decodeJwtPayload = (token) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  };

  const isTokenExpired = (token) => {
    const payload = decodeJwtPayload(token);
    if (!payload?.exp) return true;
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  };

  const refreshToken = async () => {
    const newToken = await refreshAccessToken();
    if (!newToken) return false;
    const payload = decodeJwtPayload(newToken);
    if (payload?.userId) {
      setUserId(payload.userId);
      return true;
    }
    return false;
  };

  const keepAlive = async () => {
    try {
      await api.get("/ping");
    } catch (error) {}
  };

  const loadGuestCartItems = async () => {
    const guestItems = getGuestCart();
    const guestWithStock = await Promise.all(
      guestItems.map(async (item) => {
        try {
          const product = await getProduct(item.productId, null);
          const sizeObj = product?.productSize?.find(
            (s) => s.sizeName === item.size
          );
          const availableStock = sizeObj !== undefined ? Number(sizeObj.stock) || 0 : (product?.stock !== undefined ? Number(product.stock) || 0 : 10);
          return {
            ...item,
            color: product?.productColor || "N/A",
            availableStock,
            isOutOfStock: availableStock <= 0,
            stockExceeded: item.quantity > availableStock,
          };
        } catch (_) {
          return {
            ...item,
            color: "N/A",
            availableStock: 10,
            isOutOfStock: false,
            stockExceeded: false,
          };
        }
      })
    );
    setCartItems(guestWithStock);
    setIsGuest(true);
    setLoading(false);
    setError(null);
  };

  // Initialize authentication
  useEffect(() => {
    const jwtCookie = getCookie("jwt");
    let token = jwtCookie;

    if (jwtCookie) {
      localStorage.setItem("token", jwtCookie);
    } else {
      token = localStorage.getItem("token");
    }

    const finishAsGuest = () => {
      setUserId(null);
      setIsGuest(true);
      loadGuestCartItems();
    };

    if (token) {
      if (isTokenExpired(token)) {
        refreshToken().then((refreshed) => {
          if (refreshed) {
            const newToken = localStorage.getItem("token");
            const payload = decodeJwtPayload(newToken);
            if (payload?.userId) {
              setUserId(payload.userId);
              setIsGuest(false);
              localStorage.setItem("isLoggedIn", "true");
              localStorage.setItem("userId", payload.userId);
              localStorage.setItem("userFirstName", payload.firstName || "");
            } else {
              finishAsGuest();
            }
          } else {
            finishAsGuest();
          }
        });
      } else {
        const payload = decodeJwtPayload(token);
        if (payload?.userId) {
          setUserId(payload.userId);
          setIsGuest(false);
          localStorage.setItem("isLoggedIn", "true");
          localStorage.setItem("userId", payload.userId);
          localStorage.setItem("userFirstName", payload.firstName || "");
        } else {
          finishAsGuest();
        }
      }
    } else {
      finishAsGuest();
    }

    const onGuestCartUpdate = () => {
      if (!userId) loadGuestCartItems();
    };
    window.addEventListener("aminii-guest-cart-updated", onGuestCartUpdate);

    const keepAliveInterval = setInterval(() => {
      if (localStorage.getItem("isLoggedIn") === "true") {
        keepAlive();
      }
    }, 600000);

    return () => {
      clearInterval(keepAliveInterval);
      window.removeEventListener("aminii-guest-cart-updated", onGuestCartUpdate);
    };
  }, []);

  // Fetch cart items
  useEffect(() => {
    const fetchCartItems = async () => {
      // Use localStorage as authoritative fallback — state may not have resolved yet
      const resolvedUserId = userId || localStorage.getItem("userId");

      if (!resolvedUserId) {
        if (isGuest) loadGuestCartItems();
        return;
      }

      try {
        const token = localStorage.getItem("token");
        if (token && isTokenExpired(token)) {
          const refreshed = await refreshToken();
          if (!refreshed) {
            setShowLoginPopup(true);
            setLoading(false);
            return;
          }
        }

        const cartResponse = await api.get(`/cart/user/${resolvedUserId}`);
        const cartData = cartResponse.data?.data || cartResponse.data;

        if (!Array.isArray(cartData)) {
          setCartItems([]);
        } else {
          const cartWithProductDetails = await Promise.all(
            cartData.map(async (cartItem) => {
              const product = await getProduct(cartItem.productId, token);
              const sizeObj = product?.productSize?.find(
                (s) => s.sizeName === cartItem.cartSize
              );
              const availableStock = sizeObj !== undefined ? Number(sizeObj.stock) || 0 : (product?.stock !== undefined ? Number(product.stock) || 0 : 10);
              return {
                cartId: cartItem.cartId,
                productId: cartItem.productId,
                productName: product.productName,
                productImage:
                  product.productImages?.[0] ||
                  "https://via.placeholder.com/150",
                productPrice: product.productPrice,
                beforeDiscountPrice:
                  product.beforeDiscountPrice || product.productPrice,
                size: cartItem.cartSize,
                quantity: cartItem.cartQty,
                color: product.productColor || "N/A",
                availableStock,
                isOutOfStock: availableStock <= 0,
                stockExceeded: cartItem.cartQty > availableStock,
              };
            })
          );
          setCartItems(cartWithProductDetails);
        }
        setLoading(false);
        setError(null);
      } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          const refreshed = await refreshToken();
          if (refreshed) {
            fetchCartItems();
          } else {
            setShowLoginPopup(true);
            setLoading(false);
          }
        } else if (err.response?.status === 404) {
          setCartItems([]);
          setLoading(false);
          setError(null);
        } else {
          setError(
            "An error occurred while loading the cart. Please try again."
          );
          setLoading(false);
        }
      }
    };

    fetchCartItems();
  }, [userId, isGuest]);

  // Reset promo discount and promo code when cart is empty
  useEffect(() => {
    if (cartItems.length === 0 && (promoDiscount > 0 || promoCode)) {
      setPromoDiscount(0);
      setPromoCode("");
    }
  }, [cartItems, promoDiscount, promoCode]);

  const getItemKey = (item) =>
    item.cartId ?? `${item.productId}-${item.size}`;

  const handleIncreaseQuantity = async (itemKey) => {
    const item = cartItems.find((i) => getItemKey(i) === itemKey);
    if (!item || item.quantity >= 6) return;
    setStockError(null);

    // ── Stock validation (guest path) ──
    if (isGuest) {
      try {
        const product = await getProduct(item.productId, null);
        const sizeObj = product?.productSize?.find(
          (s) => s.sizeName === item.size
        );
        const availableStock = sizeObj?.stock ?? Infinity;
        if (item.quantity + 1 > availableStock) {
          setStockError({
            itemKey,
            message: `Only ${availableStock} unit${availableStock === 1 ? "" : "s"} available for size ${item.size}.`,
          });
          return;
        }
      } catch (_) {/* ignore cache miss, proceed normally */}
      updateGuestCartQty(item.productId, item.size, item.quantity + 1);
      loadGuestCartItems();
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (token && isTokenExpired(token)) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          setShowLoginPopup(true);
          return;
        }
      }

      const item = cartItems.find((i) => getItemKey(i) === itemKey);
      if (!item) return;
      if (item.quantity >= 6) return;

      // ── Stock validation (logged-in path) ──
      try {
        const product = await getProduct(item.productId, localStorage.getItem("token"));
        const sizeObj = product?.productSize?.find(
          (s) => s.sizeName === item.size
        );
        const availableStock = sizeObj?.stock ?? Infinity;
        if (item.quantity + 1 > availableStock) {
          setStockError({
            itemKey,
            message: `Only ${availableStock} unit${availableStock === 1 ? "" : "s"} available for size ${item.size}.`,
          });
          return;
        }
      } catch (_) {/* ignore cache miss */}

      const newQuantity = item.quantity + 1;
      await api.put(`/cart/${item.cartId}`, {
        userId: parseInt(userId),
        productId: item.productId,
        cartSize: item.size,
        cartQty: newQuantity,
      });

      setCartItems((prevItems) =>
        prevItems.map((i) =>
          getItemKey(i) === itemKey ? { ...i, quantity: newQuantity } : i
        )
      );
      setError(null);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        const refreshed = await refreshToken();
        if (refreshed) {
          handleIncreaseQuantity(itemKey);
        } else {
          setShowLoginPopup(true);
        }
      } else {
        setError(
          "Failed to update quantity: " + (err.response?.data || err.message)
        );
      }
    }
  };

  const handleDecreaseQuantity = async (itemKey) => {
    const item = cartItems.find((i) => getItemKey(i) === itemKey);
    if (!item) return;

    // If quantity is 1, clicking minus should remove the item entirely
    if (item.quantity <= 1) {
      handleRemoveItem(itemKey);
      return;
    }

    if (isGuest) {
      updateGuestCartQty(item.productId, item.size, item.quantity - 1);
      loadGuestCartItems();
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (token && isTokenExpired(token)) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          setShowLoginPopup(true);
          return;
        }
      }

      const item = cartItems.find((i) => getItemKey(i) === itemKey);
      if (!item) return;

      // If quantity is 1, remove the item
      if (item.quantity <= 1) {
        handleRemoveItem(itemKey);
        return;
      }

      const newQuantity = item.quantity - 1;
      await api.put(`/cart/${item.cartId}`, {
        userId: parseInt(userId),
        productId: item.productId,
        cartSize: item.size,
        cartQty: newQuantity,
      });

      setCartItems((prevItems) =>
        prevItems.map((i) =>
          getItemKey(i) === itemKey ? { ...i, quantity: newQuantity } : i
        )
      );
      setError(null);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        const refreshed = await refreshToken();
        if (refreshed) {
          handleDecreaseQuantity(itemKey);
        } else {
          setShowLoginPopup(true);
        }
      } else {
        setError(
          "Failed to update quantity: " + (err.response?.data || err.message)
        );
      }
    }
  };

  // Remove item from cart
  const handleRemoveItem = async (itemKey) => {
    const item = cartItems.find((i) => getItemKey(i) === itemKey);
    if (!item) return;

    if (isGuest) {
      removeGuestCartItem(item.productId, item.size);
      loadGuestCartItems();
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (token && isTokenExpired(token)) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          setShowLoginPopup(true);
          return;
        }
      }

      await api.delete(`/cart/${item.cartId}`);
      setCartItems((prevItems) =>
        prevItems.filter((i) => getItemKey(i) !== itemKey)
      );
      setError(null);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        const refreshed = await refreshToken();
        if (refreshed) {
          handleRemoveItem(itemKey);
        } else {
          setShowLoginPopup(true);
        }
      } else {
        setError(
          "Failed to remove item: " + (err.response?.data || err.message)
        );
      }
    }
  };

  // Calculate totals using useMemo
  const calculations = useMemo(() => {
    const originalSubtotal = cartItems
      .reduce(
        (total, item) => total + item.beforeDiscountPrice * item.quantity,
        0
      )
      .toFixed(2);
    const subtotal = cartItems
      .reduce((total, item) => total + item.productPrice * item.quantity, 0)
      .toFixed(2);
    const initialDiscount = (
      parseFloat(originalSubtotal) - parseFloat(subtotal)
    ).toFixed(2);
    const total = (parseFloat(subtotal) - promoDiscount).toFixed(2);

    return { originalSubtotal, subtotal, initialDiscount, total };
  }, [cartItems, promoDiscount]);

  // Handle promo code submission — validates via backend
  const handlePromoCodeSubmit = async () => {
    if (!promoCode.trim()) return;
    if (isGuest) {
      setShowInvalidDiscountPopup(true);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await api.post(
        "/promo/validate",
        { code: promoCode.trim().toUpperCase(), userId: parseInt(userId) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const discount = response.data?.discount ?? 0;
      if (discount <= 0) {
        setShowInvalidDiscountPopup(true);
        setPromoDiscount(0);
        setPromoCode("");
      } else {
        const subtotal = parseFloat(calculations.subtotal);
        if (subtotal - discount <= 0) {
          setShowInvalidDiscountPopup(true);
          setPromoDiscount(0);
          setPromoCode("");
        } else {
          setPromoDiscount(discount);
        }
      }
    } catch {
      setShowInvalidDiscountPopup(true);
      setPromoDiscount(0);
      setPromoCode("");
    }
  };

  // Stock and delivery calculations
  const outOfStockCount = useMemo(() => {
    return cartItems.filter((i) => i.isOutOfStock || (i.availableStock !== undefined && i.availableStock <= 0)).length;
  }, [cartItems]);

  const stockExceededCount = useMemo(() => {
    return cartItems.filter((i) => !i.isOutOfStock && i.availableStock !== undefined && i.availableStock > 0 && i.quantity > i.availableStock).length;
  }, [cartItems]);

  const hasStockIssue = outOfStockCount > 0 || stockExceededCount > 0;

  const FREE_SHIPPING_THRESHOLD = 999;
  const currentSubtotal = parseFloat(calculations.subtotal) || 0;
  const freeShippingDiff = Math.max(0, FREE_SHIPPING_THRESHOLD - currentSubtotal);
  const freeShippingProgress = Math.min(100, Math.round((currentSubtotal / FREE_SHIPPING_THRESHOLD) * 100));

  const handleRemoveUnavailableItems = async () => {
    const itemsToRemove = cartItems.filter(
      (i) => i.isOutOfStock || (i.availableStock !== undefined && i.availableStock <= 0)
    );
    const itemsToAdjust = cartItems.filter(
      (i) => !i.isOutOfStock && i.availableStock !== undefined && i.availableStock > 0 && i.quantity > i.availableStock
    );

    for (const item of itemsToRemove) {
      if (isGuest) {
        removeGuestCartItem(item.productId, item.size);
      } else {
        try {
          await api.delete(`/cart/${item.cartId}`);
        } catch (_) {}
      }
    }

    for (const item of itemsToAdjust) {
      if (isGuest) {
        updateGuestCartQty(item.productId, item.size, item.availableStock);
      } else {
        try {
          await api.put(`/cart/${item.cartId}`, {
            userId: parseInt(userId),
            productId: item.productId,
            cartSize: item.size,
            cartQty: item.availableStock,
          });
        } catch (_) {}
      }
    }

    if (isGuest) {
      loadGuestCartItems();
    } else {
      const resolvedUserId = userId || localStorage.getItem("userId");
      const token = localStorage.getItem("token");
      if (resolvedUserId) {
        try {
          const cartResponse = await api.get(`/cart/user/${resolvedUserId}`);
          const cartData = cartResponse.data?.data || cartResponse.data;
          if (Array.isArray(cartData)) {
            const cartWithProductDetails = await Promise.all(
              cartData.map(async (cartItem) => {
                const product = await getProduct(cartItem.productId, token);
                const sizeObj = product?.productSize?.find(
                  (s) => s.sizeName === cartItem.cartSize
                );
                const availableStock = sizeObj !== undefined ? Number(sizeObj.stock) || 0 : (product?.stock !== undefined ? Number(product.stock) || 0 : 10);
                return {
                  cartId: cartItem.cartId,
                  productId: cartItem.productId,
                  productName: product.productName,
                  productImage:
                    product.productImages?.[0] ||
                    "https://via.placeholder.com/150",
                  productPrice: product.productPrice,
                  beforeDiscountPrice:
                    product.beforeDiscountPrice || product.productPrice,
                  size: cartItem.cartSize,
                  quantity: cartItem.cartQty,
                  color: product.productColor || "N/A",
                  availableStock,
                  isOutOfStock: availableStock <= 0,
                  stockExceeded: cartItem.cartQty > availableStock,
                };
              })
            );
            setCartItems(cartWithProductDetails);
          }
        } catch (_) {}
      }
    }
    setStockError(null);
  };

  // Navigation handlers
  const handleCheckout = () => {
    if (hasStockIssue) return;
    navigate("/checkout", { state: { cartItems, promoCode, promoDiscount } });
  };

  const handleStartShopping = () => {
    navigate("/productList");
  };

  const handleLoginRedirect = () => {
    document.cookie = "jwt=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/";
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("userFirstName");
    setShowLoginPopup(false);
    setUserId(null);
    navigate("/");
  };

  const handleCancelPopup = () => {
    setShowLoginPopup(false);
    navigate(-1);
  };

  const handleInvalidDiscountContinue = () => {
    setShowInvalidDiscountPopup(false);
  };

  const handleBackNavigation = () => {
    navigate("/");
  };

  const handleImageError = (e) => {
    e.target.src = "https://via.placeholder.com/150";
    e.target.onerror = null;
  };

  if (loading) {
    return (
      <div className="modern-cart-page">
        <div className="modern-cart-header">
          <h1 className="cart-title">Shopping Bag</h1>
        </div>
        <div style={{ maxWidth: "800px", margin: "20px auto", padding: "0 10px" }}>
          {[...Array(3)].map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="modern-cart-page">
      <div className="modern-cart-header">
        <button className="back-button" onClick={handleBackNavigation}>
          ×
        </button>
        <h1 className="cart-title">
          Your Bag <span className="item-count">({cartItems.length})</span>
        </h1>
      </div>

      <div className="cart-divider"></div>

      {/* Dynamic Free Shipping Progress Bar */}
      {/* {cartItems.length > 0 && (
        <div className="free-shipping-progress-box">
          <div className="free-shipping-text">
            {freeShippingDiff === 0 ? (
              <span>🎉 <strong>Congratulations!</strong> You unlocked <strong>FREE Shipping</strong>!</span>
            ) : (
              <span>Add <strong>₹{freeShippingDiff.toFixed(0)}</strong> more to qualify for <strong>FREE Shipping</strong></span>
            )}
          </div>
          <div className="free-shipping-bar-track">
            <div
              className="free-shipping-bar-fill"
              style={{ width: `${freeShippingProgress}%` }}
            />
          </div>
        </div>
      )} */}

      {/* Out of Stock Warning Banner */}
      {hasStockIssue && (
        <div className="cart-stock-issue-banner">
          <div className="stock-banner-info">
            {/* <span className="stock-banner-icon">⚠️</span> */}
            <div>
              <strong>Inventory Alert:</strong> Some items in your bag are out of stock or exceed current available stock. Please update them before checkout.
            </div>
          </div>
          <button
            className="stock-banner-resolve-btn"
            onClick={handleRemoveUnavailableItems}
          >
            Auto-Fix Bag
          </button>
        </div>
      )}

      {error && <div className="cart-error">{error}</div>}

      {cartItems.length === 0 ? (
        <div className="cart-empty">
          <h2>Your bag is empty</h2>
          <button
            onClick={handleStartShopping}
            className="continue-shopping-btn"
          >
            Continue Shopping
          </button>
        </div>
      ) : (
        <div className="cart-content">
          <button
            className="amin-explore-btn"
            onClick={() => navigate("/")}
            style={{ marginBottom: "16px" }}
          >
            Explore More
          </button>
          <div className="cart-items-list">
            {cartItems.map((item) => {
              const isItemOOS = item.isOutOfStock || (item.availableStock !== undefined && item.availableStock <= 0);
              const isQtyExceeded = !isItemOOS && item.availableStock !== undefined && item.availableStock > 0 && item.quantity > item.availableStock;
              const isMaxQty = item.quantity >= 6 || (item.availableStock !== undefined && item.quantity >= item.availableStock) || isItemOOS;

              return (
                <div
                  key={getItemKey(item)}
                  className={`cart-item-row ${isItemOOS ? "cart-item-oos" : ""} ${isQtyExceeded ? "cart-item-exceeded" : ""}`}
                >
                  <div
                    className="item-image"
                    onClick={() => item.productId && navigate(`/productDetails/${item.productId}`)}
                    style={{ cursor: "pointer", position: "relative" }}
                    title="View product details"
                  >
                    <img
                      src={getOptimizedImageUrl(item.productImage, { width: 250, colorSpace: "srgb" })}
                      alt={item.productName}
                      loading="lazy"
                      decoding="async"
                      onError={handleImageError}
                    />
                    {isItemOOS && <span className="cart-item-oos-badge">Sold Out</span>}
                  </div>
                  <div className="item-details">
                    <div
                      className="item-info"
                      onClick={() => item.productId && navigate(`/productDetails/${item.productId}`)}
                      style={{ cursor: "pointer" }}
                      title="View product details"
                    >
                      <h3 className="item-name">{item.productName}</h3>
                      <p className="item-description">
                        Size: <strong>{item.size}</strong>
                        {isItemOOS && <span className="cart-oos-text"> (Out of stock)</span>}
                        {isQtyExceeded && <span className="cart-stock-alert-text"> (Only {item.availableStock} available)</span>}
                      </p>
                    </div>
                    <div className="item-actions">
                      <div className="quantity-controls">
                        <button
                          className="quantity-btn"
                          onClick={() => {
                            setStockError(null);
                            handleDecreaseQuantity(getItemKey(item));
                          }}
                        >
                          −
                        </button>
                        <span className="quantity-value">{item.quantity}</span>
                        <button
                          className="quantity-btn"
                          onClick={() => handleIncreaseQuantity(getItemKey(item))}
                          disabled={isMaxQty}
                          title={isMaxQty ? "Maximum available quantity reached" : "Increase quantity"}
                        >
                          +
                        </button>
                      </div>
                      {stockError && stockError.itemKey === getItemKey(item) && (
                        <p style={{
                          color: "#dc2626",
                          fontSize: "11px",
                          fontWeight: 600,
                          margin: "6px 0 0",
                          lineHeight: 1.4,
                          background: "rgba(220,38,38,0.06)",
                          padding: "5px 8px",
                          borderRadius: "6px",
                          border: "1px solid rgba(220,38,38,0.2)",
                        }}>
                          ⚠️ {stockError.message}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="item-price">
                    ₹{item.productPrice}
                    {item.beforeDiscountPrice > item.productPrice && (
                      <span className="original-price">
                        ₹{item.beforeDiscountPrice}
                      </span>
                    )}
                  </div>
                  <button
                    className="remove-item-btn"
                    onClick={() => handleRemoveItem(getItemKey(item))}
                    aria-label="Remove item"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                      <path d="M10 11v6"></path>
                      <path d="M14 11v6"></path>
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>

          <div className="cart-summary-section">
          </div>
        </div>
      )}

      {cartItems.length > 0 && (
        <>
          <div className="cart-footer">
            <div className="cart-summary">
              <div className="cart-row">
                <span>Subtotal</span>
                <span className="price">₹{calculations.originalSubtotal}</span>
              </div>
              <div className="cart-row">
                <span className="price-disc">Discount</span>
                <span className="price-disc">
                  -₹
                  {(
                    parseFloat(calculations.initialDiscount) + promoDiscount
                  ).toFixed(2)}
                </span>
              </div>
              <div className="cart-row total">
                <span>Total</span>
                <span className="price">₹{calculations.total}</span>
              </div>
            </div>
            {hasStockIssue && (
              <p className="cart-checkout-block-msg">
                Please resolve out-of-stock items before checkout
              </p>
            )}
            <button
              className={`checkout-btn ${hasStockIssue ? "disabled-checkout" : ""}`}
              onClick={handleCheckout}
              disabled={cartItems.length === 0 || hasStockIssue}
            >
              {hasStockIssue ? "OUT OF STOCK ITEMS IN BAG" : "CHECKOUT"}
            </button>
          </div>
        </>
      )}

      {showLoginPopup && (
        <div className="login-popup-overlay">
          <div className="login-popup">
            <h2>Please Login</h2>
            <p>You need to be logged in to view your cart.</p>
            <div className="login-popup-buttons">
              <button onClick={handleLoginRedirect} className="login-btn">
                Login
              </button>
              <button onClick={handleCancelPopup} className="cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvalidDiscountPopup && (
        <div className="login-popup-overlay">
          <div className="login-popup">
            <h2>Invalid Discount</h2>
            <p>Discount invalid: Total cannot be zero or negative</p>
            <div className="login-popup-buttons">
              <button
                onClick={handleInvalidDiscountContinue}
                className="login-btn"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddToCart;
